import React, { useEffect, useMemo, useState } from 'react';
import { App, Breadcrumb, Button, Checkbox, Input, Popconfirm, Radio, Select, Tag, Tooltip, Typography } from 'antd';
import {
  ArrowRightOutlined, DeleteOutlined, EnterOutlined, FlagOutlined, PlusOutlined, StarFilled, WarningOutlined,
} from '@ant-design/icons';
import { optionLetter } from '../cardTypes';
import { AddButton, Field, isBlank, ItemActions, plainText, RichTextArea, Section } from '../fields';
import { moveItem, removeAt, replaceAt } from '../listOps';
import {
  buildStoryTree, deleteScene, endTypeOf, EndType, incomingLinks, newSceneKey, reachableScenes,
  renameScene, StoryCard, StoryChoice, StoryNode, StoryScene, validateSceneKey,
} from '../storyGraph';
import { CardEditorProps } from './types';

const { Text } = Typography;

const NEW_SCENE = '\u0000new-scene';

const OUTCOMES: Record<EndType, { label: string; heading: string; color: string }> = {
  good: { label: 'Good', heading: 'Лучший исход', color: 'success' },
  mid: { label: 'Mid', heading: 'Можно лучше', color: 'warning' },
  bad: { label: 'Bad', heading: 'Провал', color: 'error' },
};

const OutcomeTag: React.FC<{ scene: StoryScene }> = ({ scene }) => {
  const endType = endTypeOf(scene);
  return endType
    ? <Tag color={OUTCOMES[endType].color} style={{ marginInlineEnd: 0 }}><FlagOutlined /> {OUTCOMES[endType].label}</Tag>
    : <Tag color="error" style={{ marginInlineEnd: 0 }}><FlagOutlined /> ?</Tag>;
};

/** Ids of every expanded node, mapped to the path that reaches it in the story map. */
function canonicalPaths(tree: StoryNode | null): Record<string, string[]> {
  const paths: Record<string, string[]> = {};
  const visit = (node: StoryNode) => {
    if (node.kind !== 'scene') return;
    paths[node.key] = node.path;
    node.children.forEach(visit);
  };
  if (tree) visit(tree);
  return paths;
}

const isPrefix = (prefix: string[], path: string[]) =>
  prefix.length <= path.length && prefix.every((key, i) => path[i] === key);

// ─── Story map ───

interface TreeProps {
  node: StoryNode;
  scenes: Record<string, StoryScene>;
  startScene: string;
  selectedKey: string;
  selectedPath: string[];
  onOpen: (node: StoryNode) => void;
}

const StoryTreeNode: React.FC<TreeProps> = (props) => {
  const { node, scenes, startScene, selectedKey, selectedPath, onOpen } = props;
  const scene = node.kind === 'missing' ? undefined : scenes[node.key];
  const classes = [
    'lbe-tree-node',
    `kind-${node.kind}`,
    node.kind === 'scene' && node.key === selectedKey ? 'is-selected' : '',
    node.kind === 'scene' && isPrefix(node.path, selectedPath) ? 'is-on-path' : '',
  ].filter(Boolean).join(' ');

  const hint = node.kind === 'missing'
    ? (node.key ? `No scene "${node.key}" — click to create it` : 'This choice has no target — click to fix it')
    : node.kind === 'repeat'
      ? `Already shown above — click to open "${node.key}"`
      : plainText(scene?.text) || 'No text yet';

  return (
    <>
      <button type="button" className={classes} title={hint} onClick={() => onOpen(node)}>
        {node.via !== null && <div className="lbe-tree-via">“{plainText(node.via) || 'empty choice'}”</div>}
        <div className="lbe-tree-key">
          {node.kind === 'missing' && <WarningOutlined />}
          {node.kind === 'repeat' && <EnterOutlined style={{ transform: 'scaleX(-1)' }} />}
          <span className="lbe-tree-key-text">{node.key || 'no target'}</span>
          {node.kind === 'scene' && node.key === startScene && <Tag color="blue" style={{ marginInlineEnd: 0 }}>start</Tag>}
          {node.kind !== 'missing' && scene?.isEnd && <OutcomeTag scene={scene} />}
          {node.kind !== 'missing' && scene?.isGoodPath && <StarFilled style={{ color: '#13c2c2' }} />}
        </div>
      </button>
      {node.children.length > 0 && (
        <div className="lbe-tree-children">
          {node.children.map((child, i) => <StoryTreeNode key={i} {...props} node={child} />)}
        </div>
      )}
    </>
  );
};

// ─── Scene id ───

const SceneKeyInput: React.FC<{ value: string; onRename: (next: string) => boolean }> = ({ value, onRename }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    const next = draft.trim();
    if (next === value || !onRename(next)) setDraft(value);
  };

  return (
    <Input
      size="small"
      // Commits on blur — Ctrl/⌘+S blurs it first so a pending rename is saved (LessonsPage).
      data-commit-on-blur
      value={draft}
      prefix={<Text type="secondary" style={{ fontSize: 12 }}>id</Text>}
      style={{ maxWidth: 240 }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={commit}
    />
  );
};

// ─── Editor ───

const StoryModeEditor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const { message, modal } = App.useApp();
  const story = card as StoryCard;
  const scenes = useMemo(() => story.scenes ?? {}, [story.scenes]);
  const keys = Object.keys(scenes);
  const tree = useMemo(() => buildStoryTree(story), [story]);
  const paths = useMemo(() => canonicalPaths(tree), [tree]);
  const reachable = useMemo(() => reachableScenes(story), [story]);
  const orphans = keys.filter((key) => !reachable.has(key));
  const endings = keys.filter((key) => scenes[key].isEnd).length;

  const fallbackKey = scenes[story.startScene] ? story.startScene : keys[0] ?? '';
  const [selection, setSelection] = useState({ key: fallbackKey, path: [fallbackKey] });
  // The selected scene can disappear (deleted, renamed from JSON) — fall back to the start.
  const current = scenes[selection.key] ? selection : { key: fallbackKey, path: paths[fallbackKey] ?? [fallbackKey] };
  const sceneKey = current.key;
  const scene: StoryScene | undefined = scenes[sceneKey];

  const open = (key: string, path?: string[]) => setSelection({ key, path: path ?? paths[key] ?? [key] });
  /** Follows a choice from the current scene, extending the breadcrumb (or cutting it back on a loop). */
  const follow = (key: string) => {
    const index = current.path.indexOf(key);
    setSelection({ key, path: index >= 0 ? current.path.slice(0, index + 1) : [...current.path, key] });
  };

  const commit = (next: StoryCard) => onChange(next);
  const setScene = (key: string, next: StoryScene) => commit({ ...story, scenes: { ...scenes, [key]: next } });
  const patchScene = (patch: Partial<StoryScene>) => scene && setScene(sceneKey, { ...scene, ...patch });

  const createScene = (key = newSceneKey(scenes)) => {
    commit({ ...story, scenes: { ...scenes, [key]: { text: '' } } });
    setSelection({ key, path: [key] });
  };

  const onOpenNode = (node: StoryNode) => {
    if (node.kind === 'scene') return open(node.key, node.path);
    if (node.kind === 'repeat') return open(node.key);
    // Missing target: create it, or — for a choice with no target at all — open the scene that owns the choice.
    if (node.key) return createScene(node.key);
    const owner = node.path[node.path.length - 2];
    if (owner) open(owner);
  };

  const rename = (to: string) => {
    const error = validateSceneKey(story, sceneKey, to);
    if (error) {
      message.error(error);
      return false;
    }
    commit(renameScene(story, sceneKey, to));
    setSelection({ key: to, path: current.path.map((key) => (key === sceneKey ? to : key)) });
    return true;
  };

  if (!scene) {
    return (
      <Section title="Scenes" help="This story has no scenes yet.">
        <AddButton onClick={() => {
          commit({ ...story, startScene: 'start', scenes: { start: { text: '' } } });
          setSelection({ key: 'start', path: ['start'] });
        }}>
          Add start scene
        </AddButton>
      </Section>
    );
  }

  const choices = scene.choices ?? [];
  const incoming = incomingLinks(story, sceneKey);
  const isStart = sceneKey === story.startScene;
  const endType = endTypeOf(scene);
  const tone = scene.isGoodPath ? 'good-path' : scene.isEnd ? (endType ?? 'bad') : 'none';

  const setChoices = (next: StoryChoice[]) => patchScene({ choices: next });
  const patchChoice = (index: number, patch: Partial<StoryChoice>) =>
    setChoices(replaceAt(choices, index, { ...choices[index], ...patch }));
  const linkToNewScene = (index: number) => {
    const key = newSceneKey(scenes);
    commit({
      ...story,
      scenes: {
        ...scenes,
        [sceneKey]: { ...scene, choices: replaceAt(choices, index, { ...choices[index], next: key }) },
        [key]: { text: '' },
      },
    });
  };

  /** Switching kind drops the other kind's fields, so nothing stays stored but invisible. */
  const setKind = (kind: 'choices' | 'end') => {
    const { choices: _c, isEnd: _e, endType: _t, lesson, ...rest } = scene;
    const apply = () => setScene(sceneKey, kind === 'end'
      // No default outcome: an unset one is flagged, so the author has to pick it.
      ? { ...rest, isEnd: true, ...(scene.endType ? { endType: scene.endType } : {}), ...(lesson !== undefined ? { lesson } : {}) }
      : { ...rest, choices: [] });
    const losing = kind === 'end' ? choices.length > 0 : !isBlank(lesson);
    if (!losing) return apply();
    modal.confirm({
      title: kind === 'end' ? 'Make this scene an ending?' : 'Turn this ending into a choice scene?',
      content: kind === 'end'
        ? `Its ${choices.length} choice(s) will be removed. The scenes they lead to stay.`
        : 'The ending\'s lesson text will be removed.',
      okText: 'Continue',
      onOk: apply,
    });
  };

  const sceneOptions = [
    ...keys.map((key) => ({
      value: key,
      label: scenes[key].isEnd ? `${key} — ${OUTCOMES[endTypeOf(scenes[key]) ?? 'bad'].label.toLowerCase()} ending` : key,
    })),
    { value: NEW_SCENE, label: '+ New scene' },
  ];

  return (
    <Section
      title="Story"
      help="Click a scene in the map, or follow a choice with “Open”, to walk the story the way the user does."
    >
      <div className="lbe-story">
        {/* Map */}
        <div className="lbe-story-map">
          <div className="lbe-story-map-head">
            <Text type="secondary" style={{ fontSize: 12 }}>
              {keys.length} scene{keys.length === 1 ? '' : 's'} · {endings} ending{endings === 1 ? '' : 's'}
            </Text>
            <Tooltip title="Add a scene that nothing links to yet">
              <Button size="small" type="text" icon={<PlusOutlined />} onClick={() => createScene()}>Scene</Button>
            </Tooltip>
          </div>
          {tree ? (
            <StoryTreeNode
              node={tree}
              scenes={scenes}
              startScene={story.startScene}
              selectedKey={sceneKey}
              selectedPath={current.path}
              onOpen={onOpenNode}
            />
          ) : (
            <Text type="danger" style={{ fontSize: 12, margin: '0 4px' }}>
              Start scene “{story.startScene}” doesn't exist — open a scene and make it the start.
            </Text>
          )}
          {orphans.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="lbe-label" style={{ margin: '0 4px 4px' }}>
                <WarningOutlined style={{ color: '#faad14' }} /> Not reachable from the start
              </div>
              {orphans.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`lbe-tree-node${key === sceneKey ? ' is-selected' : ''}`}
                  onClick={() => open(key, [key])}
                >
                  <div className="lbe-tree-key">
                    <span className="lbe-tree-key-text">{key}</span>
                    {scenes[key].isEnd && <OutcomeTag scene={scenes[key]} />}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected scene */}
        <div className="lbe-fields">
          <Breadcrumb
            items={current.path.map((key, i) => ({
              title: i === current.path.length - 1
                ? <Text strong>{key}</Text>
                : <Typography.Link onClick={() => open(key, current.path.slice(0, i + 1))}>{key}</Typography.Link>,
            }))}
          />

          <div className={`lbe-scene tone-${tone}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <SceneKeyInput value={sceneKey} onRename={rename} />
              {isStart ? (
                <Tag color="blue">Start scene</Tag>
              ) : (
                <Button size="small" onClick={() => {
                  commit({ ...story, startScene: sceneKey });
                  setSelection({ key: sceneKey, path: [sceneKey] });
                }}>
                  Make start
                </Button>
              )}
              <span style={{ flex: 1 }} />
              <Popconfirm
                title={`Delete scene “${sceneKey}”?`}
                description={incoming.length > 0 ? `${incoming.length} choice(s) lead here and will need a new target.` : undefined}
                okText="Delete"
                okButtonProps={{ danger: true }}
                disabled={isStart}
                onConfirm={() => commit(deleteScene(story, sceneKey))}
              >
                <Tooltip title={isStart ? 'Make another scene the start first' : undefined}>
                  <Button size="small" danger icon={<DeleteOutlined />} disabled={isStart}>Delete</Button>
                </Tooltip>
              </Popconfirm>
            </div>

            <Field label="Scene text" required>
              <RichTextArea
                minRows={3}
                value={scene.text}
                status={isBlank(scene.text) ? 'warning' : undefined}
                onChange={(text) => patchScene({ text })}
              />
            </Field>

            <Checkbox checked={!!scene.isGoodPath} onChange={(e) => patchScene({ isGoodPath: e.target.checked || undefined })}>
              Good path <Text type="secondary">— the scene is highlighted in teal</Text>
            </Checkbox>

            <Field label="What happens next">
              <Radio.Group
                size="small"
                optionType="button"
                buttonStyle="solid"
                value={scene.isEnd ? 'end' : 'choices'}
                options={[{ label: 'The user picks a choice', value: 'choices' }, { label: 'The story ends here', value: 'end' }]}
                onChange={(e) => setKind(e.target.value)}
              />
            </Field>

            {scene.isEnd ? (
              <>
                <Field
                  label="Outcome"
                  required
                  help={endType ? `The user sees “${OUTCOMES[endType].heading}”.` : 'Not set — the app shows it as a failure.'}
                >
                  <Radio.Group
                    size="small"
                    optionType="button"
                    buttonStyle="solid"
                    value={scene.endType}
                    options={(Object.keys(OUTCOMES) as EndType[]).map((value) => ({ value, label: OUTCOMES[value].label }))}
                    onChange={(e) => patchScene({ endType: e.target.value })}
                  />
                </Field>
                <Field label="Lesson" help="Tip shown under the outcome">
                  <RichTextArea value={scene.lesson} onChange={(lesson) => patchScene({ lesson })} />
                </Field>
              </>
            ) : (
              <div className="lbe-items">
                {choices.map((choice, i) => {
                  const target = scenes[choice.next];
                  return (
                    <div key={i} className="lbe-scene-choice">
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <span className="lbe-letter" style={{ marginTop: 5 }}>{optionLetter(i)}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <RichTextArea
                            minRows={1}
                            value={choice.text}
                            placeholder="Choice text"
                            status={isBlank(choice.text) ? 'warning' : undefined}
                            onChange={(text) => patchChoice(i, { text })}
                          />
                        </div>
                        <ItemActions
                          index={i}
                          count={choices.length}
                          onMove={(to) => setChoices(moveItem(choices, i, to))}
                          onRemove={() => setChoices(removeAt(choices, i))}
                        />
                      </div>
                      <div className="lbe-scene-choice-target">
                        <Text type="secondary" style={{ fontSize: 12, marginLeft: 30 }}>leads to</Text>
                        <Select
                          size="small"
                          showSearch
                          style={{ minWidth: 240 }}
                          placeholder="Pick a scene"
                          status={target ? undefined : 'error'}
                          value={choice.next || undefined}
                          options={sceneOptions}
                          onChange={(value: string) => (value === NEW_SCENE ? linkToNewScene(i) : patchChoice(i, { next: value }))}
                        />
                        {target ? (
                          <Button size="small" type="link" onClick={() => follow(choice.next)}>
                            Open <ArrowRightOutlined />
                          </Button>
                        ) : choice.next ? (
                          <Button size="small" onClick={() => createScene(choice.next)}>Create “{choice.next}”</Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
                <AddButton onClick={() => setChoices([...choices, { text: '', next: '' }])}>Add choice</AddButton>
              </div>
            )}
          </div>

          {incoming.length > 0 && (
            <div>
              <span className="lbe-label">Reached from </span>
              {incoming.map((link) => (
                <Tag key={`${link.from}-${link.choiceIndex}`} style={{ cursor: 'pointer' }} onClick={() => open(link.from)}>
                  {link.from} · {optionLetter(link.choiceIndex)}
                </Tag>
              ))}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default StoryModeEditor;
